<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { getMySettings } from '@/api'
import { initListViewPrefs } from '@/composables/useListViewPrefs'
import { setTagStyles } from '@/todos/hashtags'
import { resolveLocale, setLocale } from '@/i18n'
import { getTelegramLanguage } from '@/telegramWebApp'
import { useAuthStore } from '@/stores/auth'
import ErrorBoundary from '@/components/ErrorBoundary.vue'
import FloatingAdd from '@/components/todos/FloatingAdd.vue'
import ToastHost from '@/components/ToastHost.vue'
import UserFooter from '@/components/UserFooter.vue'

const route = useRoute()
const auth = useAuthStore()
const { t } = useI18n()

// Once signed in, the saved language choice (if any) replaces the Telegram-client default.
watch(
  () => auth.status,
  async (status) => {
    if (status !== 'authenticated') return
    try {
      const settings = await getMySettings()
      setLocale(resolveLocale(settings.language_code, getTelegramLanguage()))
      setTagStyles(settings.tag_styles)
      initListViewPrefs(settings.list_view)
    } catch {
      // keep the Telegram-client default
      initListViewPrefs(null, false)
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="app-shell">
    <nav v-if="route.name !== 'auth' && route.name !== 'landing'" class="tabs">
      <RouterLink to="/todos">{{ t('nav.todos') }}</RouterLink>
      <RouterLink to="/categories">{{ t('nav.categories') }}</RouterLink>
      <RouterLink to="/settings">{{ t('nav.settings') }}</RouterLink>
      <RouterLink v-if="auth.isAdmin" to="/admin">{{ t('nav.admin') }}</RouterLink>
    </nav>
    <main>
      <ErrorBoundary v-slot="{ remountKey }">
        <RouterView :key="remountKey" />
      </ErrorBoundary>
    </main>
    <UserFooter />
    <FloatingAdd v-if="auth.status === 'authenticated' && route.name !== 'auth' && route.name !== 'landing'" />
    <ToastHost />
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
}

.tabs {
  display: flex;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--tg-header-bg-color);
  border-bottom: 1px solid var(--tg-secondary-bg-color);
}

.tabs a {
  text-decoration: none;
  color: var(--tg-hint-color);
  font-weight: 600;
}

.tabs a.router-link-active {
  color: var(--tg-accent-text-color);
}

main {
  padding: 1rem;
}
</style>
